import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export const DAY_NAMES = [
  'Sunday',
  'Monday', 
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];

/** GayPHX station clock — Phoenix, AZ (America/Phoenix, no DST). */
export const STATION_TIMEZONE = 'America/Phoenix';

const WEEKDAY_SHORT_TO_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/** Day of week (0–6, Sun–Sat) in the station timezone — matches API schedule keys. */
export function getStationWeekday(date: Date = new Date()): number {
  const wd = new Intl.DateTimeFormat('en-US', {
    timeZone: STATION_TIMEZONE,
    weekday: 'short',
  })
    .formatToParts(date)
    .find((p) => p.type === 'weekday')?.value;
  return wd != null && wd in WEEKDAY_SHORT_TO_INDEX ? WEEKDAY_SHORT_TO_INDEX[wd] : date.getDay();
}

/** YYYY-MM-DD in Phoenix. */
export function getStationDateString(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: STATION_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function addStationDateDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export function formatStationAirDate(ymd: string, opts: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
}): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' }).format(
    new Date(Date.UTC(y, m - 1, d, 12))
  );
}

/** Today plus the next `count-1` days in Phoenix, left to right. */
export function getStationForwardDates(count = 7, now: Date = new Date()) {
  const todayIdx = getStationWeekday(now);
  const todayYmd = getStationDateString(now);
  return Array.from({ length: count }, (_, offset) => {
    const ymd = addStationDateDays(todayYmd, offset);
    const dayIndex = (todayIdx + offset) % 7;
    return {
      name: DAY_NAMES[dayIndex],
      short: DAY_NAMES[dayIndex].slice(0, 3),
      dayIndex,
      offset,
      ymd,
      isToday: offset === 0,
    };
  });
}

/** Format seconds as mm:ss or hh:mm:ss */
export function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function formatStationDateTime(
  date: Date = new Date(),
  opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: STATION_TIMEZONE,
    timeZoneName: 'short',
  }
): string {
  return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: STATION_TIMEZONE }).format(date);
}
